export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly detail: string[]

  constructor(status: number, code: string, detail: string[], options?: { cause: unknown }) {
    super(detail[0] ?? code, options)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.detail = detail
  }
}

function isErrorBody(v: unknown): v is { error: string; detail?: unknown; reasons?: unknown } {
  return typeof v === 'object' && v !== null && typeof (v as { error?: unknown }).error === 'string'
}

function readDetail(body: unknown): string[] {
  if (!isErrorBody(body)) return []
  if (Array.isArray(body.detail)) return body.detail.map(String)
  if (!Array.isArray(body.reasons)) return []
  return body.reasons
    .map((r) =>
      typeof r === 'object' &&
      r !== null &&
      typeof (r as { message?: unknown }).message === 'string'
        ? (r as { message: string }).message
        : null,
    )
    .filter((m): m is string => m !== null)
}

const RETIRED_CODES: readonly string[] = ['res_network']

function dropRetiredCodes(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value
      .filter((v) => !(typeof v === 'string' && RETIRED_CODES.includes(v)))
      .map(dropRetiredCodes)
  }
  if (typeof value === 'object' && value !== null) {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) out[k] = dropRetiredCodes(v)
    return out
  }
  return value
}

function isJsonBody(body: BodyInit | null | undefined): boolean {
  return body !== undefined && body !== null && !(body instanceof FormData)
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      ...init,
      // 본문이 있을 때만 JSON 이라고 선언한다 — 빈 POST 에 붙이면 Fastify 가
      // FST_ERR_CTP_EMPTY_JSON_BODY 로 400 을 낸다(repo CLAUDE.md §3-7).
      // FormData 도 손대지 않는다 — multipart 는 헤더에 boundary 가 들어가야 하는데 그 값은
      // 브라우저만 안다. 우리가 덮어쓰면 서버가 본문을 못 가른다.
      headers: {
        ...(isJsonBody(init?.body) ? { 'content-type': 'application/json' } : {}),
        ...init?.headers,
      },
    })
  } catch (cause) {
    // 네트워크 실패를 조용히 삼키지 않는다 — 화면이 빈 목록으로 오해하면 안 된다.
    throw new ApiError(
      0,
      'network_error',
      ['서버에 연결하지 못했습니다. 잠시 후 다시 시도해 주세요.'],
      { cause },
    )
  }

  const text = await res.text()
  const body: unknown = text.length > 0 ? JSON.parse(text) : null

  if (!res.ok) {
    const code = isErrorBody(body) ? body.error : 'unknown_error'
    const detail = readDetail(body)
    throw new ApiError(res.status, code, detail)
  }

  return dropRetiredCodes(body) as T
}

export function qs(params: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') sp.set(k, String(v))
  }
  const s = sp.toString()
  return s.length > 0 ? `?${s}` : ''
}
